import React, { Dispatch, SetStateAction, useEffect } from 'react';
import styles from './UnitsFilterPopup.module.css';
import ReactSlider from 'react-slider';
import iconClose from '../../../images/icons/icon-close.svg';
import iconBeds from '../../../images/icons/icon-beds.svg';
import iconBathroom from '../../../images/icons/icon-bathtub.svg';
import iconDollar from '../../../images/icons/icon-Dollar.svg';
import iconSquareFeet from '../../../images/icons/icon-square-feet.svg';
import iconSortBy from '../../../images/icons/icon-sortby.svg';
import { bathrooms, bedrooms, sortBy } from '../../../constants';
import { AppDispatch, IRootState } from '../../../redux/rootReducer';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { getUnitsByLocationId } from '../../../redux/unit/actions/unitsByLocationId.action';

interface props {
    setOpen: Dispatch<SetStateAction<boolean>>;
    filters: {
        bed: (string | number)[];
        bath: number[];
        sortBy: string;
        sortOrder: string;
        minPrice: number;
        maxPrice: number;
        minSize: number;
        maxSize: number;
    };
    setFilters: (arg0: {
        bed: (string | number)[] | never[] | (string | number)[];
        bath: never[] | number[] | (string | number)[];
        sortBy: string;
        sortOrder: string;
        minPrice: string | number;
        maxPrice: string | number;
        minSize: string | number;
        maxSize: string | number;
    }) => void;
    open: boolean;
    onApplyFilters?: (filters: any) => void; // Callback to notify parent when filters are applied
}

const UnitsFilterPopup = ({ setOpen, filters, setFilters, open, onApplyFilters }: props) => {
    const dispatch: AppDispatch = useDispatch();
    const locationId = useParams().id;

    // Location Filters State
    const { data: locationFiltersData } = useSelector((state: IRootState) => state.locationReducer.locationFilters);
    const locationdetail = useSelector((state: IRootState) => state.locationReducer.locationById.data?.location);

    // Extract filter data from API response
    const filterData = locationFiltersData?.data || locationFiltersData || null;
    const hasRooms = filterData?.rooms && Array.isArray(filterData.rooms) && filterData.rooms.length > 0;
    const hasBaths = filterData?.baths && Array.isArray(filterData.baths) && filterData.baths.length > 0;
    const hasPrice = filterData?.price && typeof filterData.price === 'object' && filterData.price.min !== undefined && filterData.price.max !== undefined;
    const hasSize = filterData?.size && typeof filterData.size === 'object' && filterData.size.min !== undefined && filterData.size.max !== undefined;
    const hasSortBy = filterData?.sortBy && Array.isArray(filterData.sortBy) && filterData.sortBy.length > 0;

    // Filter bedrooms options based on API response
    const availableBedrooms = hasRooms
        ? bedrooms.filter(bed =>
            bed.value === '' || filterData.rooms.map(Number).includes(Number(bed.value))
        )
        : [];

    // Create dynamic bathroom options from API response (handles decimals like 2.5)
    const availableBathrooms = hasBaths && filterData?.baths
        ? filterData.baths
            .slice()
            .sort((a: number, b: number) => a - b) // Sort ascending
            .map((bathValue: number) => {
                // Check if it exists in constants first
                const existingBath = bathrooms.find(b => b.value === bathValue);
                if (existingBath) {
                    return existingBath;
                }
                // Create dynamic entry for values not in constants (e.g., 2.5)
                const isInteger = Number.isInteger(bathValue);
                const displayValue = isInteger ? Math.floor(bathValue) : bathValue;
                return {
                    name: `${displayValue} ${bathValue === 1 ? 'Bathroom' : 'Bathrooms'}`,
                    id: `bathroom-${bathValue.toString().replace('.', '-')}`,
                    value: bathValue,
                };
            })
        : [];

    // Filter sortBy options based on API response
    const availableSortBy = hasSortBy
        ? sortBy.filter(sort => filterData.sortBy.includes(sort.name))
        : sortBy;

    const resetObj = {
        minPrice: hasPrice ? (filterData?.price?.min || '') : '',
        maxPrice: hasPrice ? (filterData?.price?.max || '') : '',
        bed: [],
        minSize: hasSize ? (filterData?.size?.min || 0) : '',
        maxSize: hasSize ? (filterData?.size?.max || 10000) : '',
        bath: [],
        sortBy: locationdetail?.isPriceListed ? 'price' : 'rooms',
        sortOrder: 'ASC',
    };

    // Set default sortBy based on available options
    if (hasSortBy && availableSortBy.length > 0 && resetObj.sortBy) {
        const defaultSort = locationdetail?.isPriceListed
            ? availableSortBy.find(s => s.sortOrder === 'price') || availableSortBy[0]
            : availableSortBy.find(s => s.sortOrder === 'rooms') || availableSortBy[0];

        if (defaultSort) {
            resetObj.sortBy = defaultSort.sortOrder;
            resetObj.sortOrder = defaultSort.sort;
        }
    }

    useEffect(() => {
        // Only reset filters when popup closes without applying (when user clicks close button)
        // Don't reset when popup closes after applying filters (handled by submitHandler)
        if (open === false) {
            // Note: This resets when popup closes without applying
            // If filters were applied via submitHandler, they should be preserved
            // The reset happens only if user closes without applying
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const handleBedroomChecked = (value: number | string, key: string) => {
        let prev = key === 'bedrooms' ? filters.bed : filters.bath;
        // Use strict comparison - 1 and 1.0 are treated as different values
        const findIndex = prev.findIndex(v => v === value);
        if (findIndex !== -1) {
            prev.splice(findIndex, 1);
        } else {
            if (value === '') {
                prev = [];
            } else {
                prev.push(value);
            }
        }
        key === 'bedrooms' ? setFilters({ ...filters, bed: [...prev] }) : setFilters({ ...filters, bath: [...prev] });
    };

    const resetHandler = () => {
        // Reset filters based on API data if available, otherwise use resetObj
        if (filterData) {
            const resetFilters = {
                minPrice: hasPrice ? filterData.price.min : '',
                maxPrice: hasPrice ? filterData.price.max : '',
                bed: [],
                minSize: hasSize ? filterData.size.min : 0,
                maxSize: hasSize ? filterData.size.max : 10000,
                bath: [],
                sortBy: locationdetail?.isPriceListed ? 'price' : 'rooms',
                sortOrder: 'ASC',
            };

            // Set default sortBy based on available options
            if (hasSortBy && availableSortBy.length > 0) {
                const defaultSort = locationdetail?.isPriceListed
                    ? availableSortBy.find(s => s.sortOrder === 'price') || availableSortBy[0]
                    : availableSortBy.find(s => s.sortOrder === 'rooms') || availableSortBy[0];

                if (defaultSort) {
                    resetFilters.sortBy = defaultSort.sortOrder;
                    resetFilters.sortOrder = defaultSort.sort;
                }
            }

            setFilters(resetFilters);
        } else {
            setFilters(resetObj);
        }
    };

    const submitHandler = async () => {
        // Notify parent component to update main filters state
        if (onApplyFilters) {
            onApplyFilters(filters);
        }
        // Dispatch API call with current popup filters
        dispatch(getUnitsByLocationId(locationId, filters));
        await setOpen(false);
    };

    return (
        <div className={styles.filterWrapper}>
            <div className={styles.header}>
                <h2>Filter</h2>
                <img
                    className={styles.iconClose}
                    onClick={() => {
                        setOpen(false);
                    }}
                    src={iconClose}
                    alt="Close"
                />
            </div>
            <div className={styles.body}>
                {/* Sort By Filter - Only show if API has sortBy data */}
                {hasSortBy && availableSortBy.length > 0 && (
                    <div className={styles.filterContainer}>
                        <h3 className={styles.filterTitle}>
                            <img className={styles.filterIcon} src={iconSortBy} alt="Sort By" /> Sort By
                        </h3>
                        <div className={styles.sortWrapper}>
                            {availableSortBy.map(({ name, sort, sortOrder }, index) => (
                                <span
                                    onClick={() => setFilters({ ...filters, sortBy: sortOrder, sortOrder: sort })}
                                    className={filters.sortBy === sortOrder && filters.sortOrder === sort ? styles.sort : `${styles.sortTitle}`}
                                    key={index}>
                                    {name}
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bedrooms Filter - Only show if API has rooms data */}
                {hasRooms && availableBedrooms.length > 0 && (
                    <div className={styles.filterContainer}>
                        <h3 className={styles.filterTitle}>
                            <img className={styles.filterIcon} src={iconBeds} alt="Beds" /> Beds
                        </h3>
                        <div className={styles.checkboxContainer}>
                            {availableBedrooms.map(({ name, id, value }, index) => (
                                <div key={id} className={styles.boxes}>
                                    <input
                                        type="checkbox"
                                        id={`${id}+${index}`}
                                        checked={filters.bed.length > 0 ? filters.bed?.includes(value) : value === ''}
                                        onChange={() => handleBedroomChecked(value, 'bedrooms')}
                                    />
                                    <label htmlFor={`${id}+${index}`}>{name}</label>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Bathrooms Filter - Only show if API has baths data */}
                {hasBaths && availableBathrooms.length > 0 && (
                    <div className={styles.filterContainer}>
                        <h3 className={styles.filterTitle}>
                            <img className={styles.filterIcon} src={iconBathroom} alt="bathroom" /> Bathroom
                        </h3>
                        <div className={styles.checkboxContainer}>
                            {availableBathrooms.map((item: { name: string; id: string; value: number | string }, index: number) => {
                                // Use strict comparison - 1 and 1.0 are treated as different values
                                const isChecked = filters.bath?.some(bathValue => bathValue === item.value);
                                return (
                                    <div key={index} className={styles.boxes}>
                                        <input
                                            type="checkbox"
                                            id={`${item.id}+${index}`}
                                            checked={!!isChecked}
                                            onChange={() => handleBedroomChecked(item.value, 'bathrooms')}
                                        />
                                        <label htmlFor={`${item.id}+${index}`}>{item.name}</label>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Price Filter - Only show if API has price data */}
                {hasPrice && (
                    <div className={styles.filterContainer}>
                        <h3 className={styles.filterTitle}>
                            <img className={styles.filterIcon} src={iconDollar} alt="Price" /> Select Price
                        </h3>
                        <div className={styles.rangeSliderWrapper}>
                            <ReactSlider
                                className="horizontal-slider"
                                thumbClassName="thumb"
                                trackClassName="track"
                                min={filterData.price.min}
                                max={filterData.price.max}
                                onAfterChange={(e) => setFilters({ ...filters, minPrice: e[0], maxPrice: e[1] })}
                                value={[Number(filters?.minPrice) || filterData.price.min, Number(filters?.maxPrice) || filterData.price.max]}
                                ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
                                renderThumb={(props, state) => <div {...props}>{`$${state.valueNow}`}</div>}
                                pearling
                                minDistance={10}
                            />
                            <p></p>
                        </div>
                    </div>
                )}

                {/* Square Footage Filter - Only show if API has size data */}
                {hasSize && (
                    <div className={styles.filterContainer}>
                        <h3 className={styles.filterTitle}>
                            <img className={styles.filterIcon} src={iconSquareFeet} alt="Square Feet" /> Select Square Foot
                        </h3>
                        <div className={styles.rangeSliderWrapper}>
                            <ReactSlider
                                className="horizontal-slider"
                                thumbClassName="thumb"
                                trackClassName="track"
                                min={filterData.size.min}
                                max={filterData.size.max}
                                onAfterChange={(e) => setFilters({ ...filters, minSize: e[0], maxSize: e[1] })}
                                value={[Number(filters?.minSize) || filterData.size.min, Number(filters?.maxSize) || filterData.size.max]}
                                ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
                                renderThumb={(props, state) => <div {...props}>{`${state.valueNow}`}</div>}
                                pearling
                                minDistance={10}
                            />
                            <p></p>
                        </div>
                    </div>
                )}

                <div className={styles.btnWrapper}>
                    <button onClick={submitHandler}>Apply Filter</button>
                    <button onClick={resetHandler} className={styles.resetFilter}>
                        Reset Filter
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnitsFilterPopup;
